/**
 * DeleteOrgDialog Component
 *
 * Confirmation dialog for deleting an organisation.
 * Requires password entry for verification before deletion proceeds.
 */

import { useCallback, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertTriangle } from 'lucide-react';
import type { IDeleteOrgDialogProps } from '@/@types/organisation';

// NEW_FUNCTION_JUSTIFIED: dedicated dialog for password-confirmed org deletion — no existing component handles this
export function DeleteOrgDialog({ orgName, isOpen, onClose, onConfirm }: IDeleteOrgDialogProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!password.trim()) return;
    setError(null);
    setIsDeleting(true);
    try {
      await onConfirm(password);
    } catch (err) {
      const convexData = (err as { data?: unknown })?.data;
      if (typeof convexData === 'string') {
        setError(convexData);
      } else if (err instanceof Error) {
        const match = err.message.match(/ConvexError:\s*(.+?)(?:\s+at\s|$)/);
        setError(match?.[1] ?? 'Failed to delete organisation');
      } else {
        setError('Failed to delete organisation');
      }
    } finally {
      setIsDeleting(false);
    }
  }, [password, onConfirm]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isDeleting) {
        setPassword('');
        setError(null);
        onClose();
      }
    },
    [isDeleting, onClose]
  );

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/30">
              <AlertTriangle
                aria-hidden="true"
                className="h-5 w-5 text-red-600 dark:text-red-400"
              />
            </div>
            <AlertDialogTitle>Delete Organisation</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            This will permanently delete <strong>{orgName}</strong>, remove all members, and cancel
            all pending invitations. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="delete-org-password">Enter your password to confirm</Label>
          <Input
            id="delete-org-password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Your login password"
            disabled={isDeleting}
            onKeyDown={e => {
              if (e.key === 'Enter' && password.trim()) {
                void handleConfirm();
              }
            }}
          />
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950 dark:text-red-400"
          >
            {error}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!password.trim() || isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete Organisation'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
